/**
 * Date Parsing Service
 * -------------------------
 * This service provides utilities for parsing and comparing dates,
 * especially handling the complex date formats used in the ThinkGPT ecosystem.
 */

/**
 * Parse a date string that might be in various formats including the complex
 * format used in ThinkGPT: 'Month Day, Year at Time Timezone'
 * 
 * @param {string} dateStr - The date string to parse
 * @returns {Date} A JavaScript Date object, or Invalid Date if parsing fails
 */
export function parseDate(dateStr) {
  if (!dateStr) return new Date(NaN); // Return invalid date for empty input
  
  try {
    // Handle complex date formats like 'May 1, 2025 at 3:02:09 PM UTC'
    if (dateStr.includes(' at ')) {
      // Format: 'Month Day, Year at Time Timezone'
      const dateParts = dateStr.split(' at ');
      const simpleDateStr = dateParts[0]; // e.g., 'May 1, 2025'
      
      // Create a simpler date string that JavaScript can reliably parse
      return new Date(simpleDateStr);
    }
    
    // Standard date format
    return new Date(dateStr);
  } catch (error) {
    console.error('[DateParsing] Error parsing date:', error);
    return new Date(NaN); // Return invalid date on error
  }
}

/**
 * Check if a date string represents a date that has already passed
 * 
 * @param {string} dateStr - The date string to check
 * @returns {boolean} True if the date has passed, false otherwise or if parsing fails
 */
export function isDateExpired(dateStr) {
  const date = parseDate(dateStr);
  const now = new Date();
  
  // If date is invalid, consider it not expired
  if (isNaN(date.getTime())) {
    console.log(`[DateParsing] Invalid date format: ${dateStr}, considering not expired`);
    return false;
  }
  
  return date < now;
}

/**
 * Format a date for display
 * 
 * @param {Date|string} date - Date object or string to format
 * @param {Object} options - Formatting options (same as toLocaleDateString options)
 * @returns {string} Formatted date string
 */
export function formatDate(date, options = {}) {
  const defaultOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  
  const mergedOptions = { ...defaultOptions, ...options };
  
  try {
    // If date is a string, parse it first
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    return dateObj.toLocaleDateString('en-US', mergedOptions);
  } catch (error) {
    console.error('[DateParsing] Error formatting date:', error);
    return 'Error';
  }
}
